import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff, Save, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const TIPOLOGIE = ["bollicine", "bianco", "rosato", "rosso", "dolce"];
const CATEGORIE = ["vino", "distillato", "liquore", "birra", "altro"];

/* Admin — gestione sezioni per Modalità Cartacea.
 *
 * Layout: lista ordinata (ordine crescente). Ogni riga espandibile con:
 *   - Titolo (editabile)
 *   - Criteri: chip toggle per tipologia + categoria + free-text per paesi + zone (comma-separated)
 *   - Flag attiva + catch_all (readonly per la sezione catch-all seed)
 *   - Bottoni: su/giù (reorder), duplica, elimina (non per catch_all), salva
 *
 * Il matching è documentato inline: "Un vino appare nella PRIMA sezione i
 * cui criteri corrispondono; se non ne matcha nessuna finisce in Altri Vini."
 */

export default function AdminPaperSections() {
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // full section object being edited
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/admin/paper-sections")
            .then(({ data }) => setSections(data.items || []))
            .catch(() => toast.error("Errore caricamento sezioni"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const move = async (sec, direction) => {
        const idx = sections.findIndex((s) => s.id === sec.id);
        const swapWith = idx + direction;
        if (swapWith < 0 || swapWith >= sections.length) return;
        const reordered = [...sections];
        [reordered[idx], reordered[swapWith]] = [reordered[swapWith], reordered[idx]];
        // Renumber order in steps of 10
        const items = reordered.map((s, i) => ({ id: s.id, order: (i + 1) * 10 }));
        try {
            await api.post("/admin/paper-sections/reorder", { items });
            load();
        } catch { toast.error("Errore reorder"); }
    };

    const toggleActive = async (sec) => {
        try {
            await api.put(`/admin/paper-sections/${sec.id}`, { active: !sec.active });
            load();
        } catch { toast.error("Errore aggiornamento"); }
    };

    const deleteSection = async (sec) => {
        if (sec.catch_all) return;
        if (!confirm(`Eliminare la sezione "${sec.title}"?`)) return;
        try {
            await api.delete(`/admin/paper-sections/${sec.id}`);
            toast.success("Sezione eliminata");
            load();
        } catch { toast.error("Errore eliminazione"); }
    };

    const createNew = () => {
        setEditing({
            id: null,
            title: "",
            active: true,
            catch_all: false,
            criteria: { tipologie: [], paesi: [], zone: [], categorie: [] },
        });
    };

    const startEdit = (sec) => {
        setEditing({
            ...sec,
            criteria: {
                tipologie: [...(sec.criteria?.tipologie || [])],
                paesi: [...(sec.criteria?.paesi || [])],
                zone: [...(sec.criteria?.zone || [])],
                categorie: [...(sec.criteria?.categorie || [])],
            },
        });
    };

    const saveEdit = async () => {
        if (!editing) return;
        if (!editing.title || !editing.title.trim()) {
            toast.error("Titolo obbligatorio");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                title: editing.title.trim(),
                active: editing.active,
                catch_all: editing.catch_all,
                criteria: editing.criteria,
            };
            if (editing.id) {
                await api.put(`/admin/paper-sections/${editing.id}`, payload);
                toast.success("Sezione aggiornata");
            } else {
                await api.post("/admin/paper-sections", payload);
                toast.success("Sezione creata");
            }
            setEditing(null);
            load();
        } catch (e) {
            toast.error("Errore salvataggio");
        } finally { setSaving(false); }
    };

    const toggleCriterion = (list, value) => {
        const arr = editing.criteria[list] || [];
        const has = arr.includes(value);
        const next = has ? arr.filter((v) => v !== value) : [...arr, value];
        setEditing({ ...editing, criteria: { ...editing.criteria, [list]: next } });
    };

    return (
        <div className="co-container py-8" data-testid="admin-paper-sections-page">
            <div className="flex items-baseline gap-3 justify-between mb-6">
                <div>
                    <span className="co-caption block mb-1">Amministrazione</span>
                    <h1 className="text-3xl sm:text-4xl">Sezioni Carta</h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl">
                        Definisce come i vini vengono raggruppati nella <em>Modalità Cartacea</em> pubblica
                        e nella stampa admin. Un vino compare nella <b>prima</b> sezione i cui criteri
                        corrispondono. I vini non catturati finiscono nella sezione <em>catch-all</em>.
                    </p>
                </div>
                <button className="co-btn-primary text-sm" onClick={createNew}
                    data-testid="admin-paper-sections-create-btn">
                    <Plus className="w-4 h-4"/> Nuova sezione
                </button>
            </div>

            {loading && <div className="italic co-caption">Caricamento…</div>}

            {!loading && !editing && (
                <ul className="space-y-2" data-testid="admin-paper-sections-list">
                    {sections.map((s, i) => (
                        <li key={s.id}
                            className={`bg-white border border-[hsl(var(--border))] rounded-sm p-4 flex items-start gap-3 ${s.active ? "" : "opacity-60"}`}
                            data-testid={`admin-paper-section-${s.id}`}>
                            <div className="flex flex-col gap-1">
                                <button onClick={() => move(s, -1)} disabled={i === 0}
                                    className="disabled:opacity-30 hover:text-[hsl(var(--primary))]"
                                    data-testid={`admin-paper-section-up-${s.id}`}>
                                    <ChevronUp className="w-4 h-4" strokeWidth={1.5}/>
                                </button>
                                <button onClick={() => move(s, +1)} disabled={i === sections.length - 1}
                                    className="disabled:opacity-30 hover:text-[hsl(var(--primary))]"
                                    data-testid={`admin-paper-section-down-${s.id}`}>
                                    <ChevronDown className="w-4 h-4" strokeWidth={1.5}/>
                                </button>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3">
                                    <span className="co-caption text-xs">#{i + 1}</span>
                                    <span className="text-base font-medium">{s.title}</span>
                                    {s.catch_all && (
                                        <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[hsl(var(--secondary))] rounded-sm text-[hsl(var(--muted-foreground))]">
                                            catch-all
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 space-x-2">
                                    <span>Tipologie: {(s.criteria?.tipologie || []).join(", ") || "—"}</span>
                                    <span>· Categorie: {(s.criteria?.categorie || []).join(", ") || "—"}</span>
                                    <span>· Paesi: {(s.criteria?.paesi || []).join(", ") || "—"}</span>
                                    <span>· Zone: {(s.criteria?.zone || []).join(", ") || "—"}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => toggleActive(s)} data-testid={`admin-paper-section-toggle-${s.id}`}>
                                    {s.active
                                        ? <Eye className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5}/>
                                        : <EyeOff className="w-4 h-4 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5}/>}
                                </button>
                                <button className="co-btn-ghost text-xs" onClick={() => startEdit(s)}
                                    data-testid={`admin-paper-section-edit-${s.id}`}>
                                    Modifica
                                </button>
                                {!s.catch_all && (
                                    <button className="text-[hsl(var(--destructive))] hover:opacity-70"
                                        onClick={() => deleteSection(s)}
                                        data-testid={`admin-paper-section-delete-${s.id}`}>
                                        <Trash2 className="w-4 h-4" strokeWidth={1.5}/>
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {editing && (
                <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-6 max-w-2xl"
                    data-testid="admin-paper-section-editor">
                    <h2 className="text-xl mb-4">
                        {editing.id ? "Modifica sezione" : "Nuova sezione"}
                    </h2>
                    <div className="space-y-4">
                        <div>
                            <label className="co-caption block mb-1">Titolo</label>
                            <Input value={editing.title}
                                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                                placeholder="Es. I Rossi di Toscana"
                                data-testid="admin-paper-section-title-input"/>
                        </div>

                        <div>
                            <label className="co-caption block mb-2">Tipologie (vuoto = qualunque)</label>
                            <div className="flex flex-wrap gap-2">
                                {TIPOLOGIE.map((t) => {
                                    const active = editing.criteria.tipologie.includes(t);
                                    return (
                                        <button key={t} type="button"
                                            onClick={() => toggleCriterion("tipologie", t)}
                                            className={`px-3 py-1 border rounded-full text-xs uppercase tracking-wider transition-colors ${active ? "bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white" : "bg-white border-[hsl(var(--border))]"}`}
                                            data-testid={`admin-paper-section-tipologia-${t}`}>
                                            {t}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <label className="co-caption block mb-2">Categorie (vuoto = qualunque)</label>
                            <div className="flex flex-wrap gap-2">
                                {CATEGORIE.map((c) => {
                                    const active = editing.criteria.categorie.includes(c);
                                    return (
                                        <button key={c} type="button"
                                            onClick={() => toggleCriterion("categorie", c)}
                                            className={`px-3 py-1 border rounded-full text-xs uppercase tracking-wider transition-colors ${active ? "bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white" : "bg-white border-[hsl(var(--border))]"}`}
                                            data-testid={`admin-paper-section-categoria-${c}`}>
                                            {c}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <label className="co-caption block mb-1">Paesi (comma-separated, es. "Italia, Francia")</label>
                            <Input value={editing.criteria.paesi.join(", ")}
                                onChange={(e) => setEditing({
                                    ...editing,
                                    criteria: {
                                        ...editing.criteria,
                                        paesi: e.target.value.split(",").map((v) => v.trim()).filter(Boolean),
                                    },
                                })}
                                placeholder="Vuoto = qualunque"
                                data-testid="admin-paper-section-paesi-input"/>
                        </div>

                        <div>
                            <label className="co-caption block mb-1">Zone / regioni (comma-separated)</label>
                            <Input value={editing.criteria.zone.join(", ")}
                                onChange={(e) => setEditing({
                                    ...editing,
                                    criteria: {
                                        ...editing.criteria,
                                        zone: e.target.value.split(",").map((v) => v.trim()).filter(Boolean),
                                    },
                                })}
                                placeholder="Es. Toscana, Piemonte — vuoto = qualunque"
                                data-testid="admin-paper-section-zone-input"/>
                        </div>

                        <div className="flex items-center gap-6">
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })}
                                    data-testid="admin-paper-section-active-switch"/>
                                Attiva
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-sm"
                                title="Cattura i vini non matchati dalle altre sezioni">
                                <Switch checked={editing.catch_all}
                                    onCheckedChange={(v) => setEditing({ ...editing, catch_all: v })}
                                    disabled={editing.id && editing.catch_all}
                                    data-testid="admin-paper-section-catchall-switch"/>
                                Catch-all
                            </label>
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button className="co-btn-primary text-sm" disabled={saving} onClick={saveEdit}
                                data-testid="admin-paper-section-save-btn">
                                <Save className="w-4 h-4"/>{saving ? "Salvo…" : "Salva"}
                            </button>
                            <button className="co-btn-ghost text-sm" onClick={() => setEditing(null)}
                                data-testid="admin-paper-section-cancel-btn">
                                <X className="w-4 h-4"/> Annulla
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
